import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { apiGet, apiPost, API } from "@/lib/api";
import { Logo, LogoWhite } from "@/components/brand/Logo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Download, Share2, Printer, ArrowRight, Check, X, Minus, Network, ScrollText, Users, Wallet,
  TrendingUp, Plug, ShieldCheck, LayoutGrid, Sparkles, Circle, RefreshCw, Mail, Loader2, Camera,
} from "lucide-react";

const ICONS = { network: Network, scroll: ScrollText, users: Users, wallet: Wallet, trending: TrendingUp, plug: Plug, shield: ShieldCheck, layout: LayoutGrid };
const reveal = { initial: { opacity: 0, y: 18 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, amount: 0.25 }, transition: { duration: 0.5, ease: [0, 0, 0.35, 1] } };

export default function Brochure() {
  const [lt] = useState(() => localStorage.getItem("navitra-landing-theme") || "lt-sky");
  const initIndustry = (() => { try { return new URLSearchParams(window.location.search).get("industry") || "general"; } catch { return "general"; } })();
  const [industry, setIndustry] = useState(initIndustry);
  const [d, setD] = useState(null);
  useEffect(() => {
    apiGet(`/brochure?industry=${industry}`).then(setD).catch(() => toast.error("Could not load brochure"));
  }, [industry]);

  const pdfUrl = `${API}/brochure.pdf${industry !== "general" ? `?industry=${industry}` : ""}`;
  const shareUrl = `${window.location.origin}/brochure${industry !== "general" ? `?industry=${industry}` : ""}`;
  const share = async () => {
    try {
      if (navigator.share) { await navigator.share({ title: "Navitra OS", text: "The operating system for enterprise AI governance.", url: shareUrl }); }
      else { await navigator.clipboard.writeText(shareUrl); toast.success("Share link copied to clipboard"); }
    } catch { /* cancelled */ }
  };
  const download = () => window.open(pdfUrl, "_blank");

  const [user] = useState(() => { try { return JSON.parse(localStorage.getItem("navitra-user") || "null"); } catch { return null; } });
  const isAdmin = !!(user && ["platform_owner", "super_admin"].includes(user.role_key));
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailVal, setEmailVal] = useState("");
  const [sending, setSending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const sendEmail = async () => {
    setSending(true);
    try {
      const res = await apiPost("/brochure/email", { email: emailVal, industry });
      if (res.ok) { toast.success(res.message || "Brochure sent."); setEmailOpen(false); setEmailVal(""); }
      else { toast.info(res.message || "Email isn't configured yet."); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send email.");
    } finally { setSending(false); }
  };

  const refreshShots = async () => {
    setRefreshing(true);
    try {
      const res = await apiPost("/brochure/refresh-shots", { actor_role_key: user?.role_key });
      toast.success(res.message || "Refreshing screenshots…");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not refresh screenshots.");
    } finally { setTimeout(() => setRefreshing(false), 2000); }
  };

  if (!d) return <div className={`${lt} min-h-screen bg-background flex items-center justify-center text-muted-foreground`}><RefreshCw className="h-5 w-5 animate-spin mr-2" />Loading brochure…</div>;

  const complianceScore = parseFloat(String((d.metrics.find((m) => m.label.includes("Compliance")) || {}).value || "0").replace("%", "")) || 0;

  return (
    <div className={`${lt} landing-wash min-h-screen bg-background text-foreground`}>
      <div className="relative z-[1]">
        {/* Header */}
        <header className="sticky top-0 z-30 glass-bar border-b border-border/60 print:hidden">
          <div className="max-w-6xl mx-auto px-5 h-16 flex items-center gap-3">
            <Link to="/" data-testid="brochure-home"><Logo className="h-12" /></Link>
            <div className="ml-auto flex items-center gap-2">
              {isAdmin && (
                <Button variant="ghost" size="sm" onClick={refreshShots} disabled={refreshing} data-testid="brochure-refresh-shots" className="rounded-full" title="Recapture product screenshots">
                  {refreshing ? <Loader2 className="h-4 w-4 animate-spin sm:mr-1.5" /> : <Camera className="h-4 w-4 sm:mr-1.5" />}<span className="hidden sm:inline">Refresh shots</span>
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={share} data-testid="brochure-share" className="rounded-full"><Share2 className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Share</span></Button>
              <Button variant="ghost" size="sm" onClick={() => setEmailOpen(true)} data-testid="brochure-email-open" className="rounded-full hidden sm:inline-flex"><Mail className="h-4 w-4 mr-1.5" />Email</Button>
              <Button variant="ghost" size="sm" onClick={() => window.print()} data-testid="brochure-print" className="rounded-full hidden md:inline-flex"><Printer className="h-4 w-4 mr-1.5" />Print</Button>
              <Button variant="outline" size="sm" onClick={download} data-testid="brochure-download" className="rounded-full"><Download className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Download PDF</span></Button>
              <Button asChild size="sm" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"><Link to="/login" data-testid="brochure-launch">Launch <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
            </div>
          </div>
        </header>

        {/* Email the brochure dialog */}
        <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
          <DialogContent data-testid="brochure-email-dialog">
            <DialogHeader>
              <DialogTitle>Send the brochure to your inbox</DialogTitle>
              <DialogDescription>We'll email you the {d.industry.label} brochure as a branded PDF.</DialogDescription>
            </DialogHeader>
            <div className="space-y-2 py-1">
              <Label htmlFor="broc-email">Work email</Label>
              <Input id="broc-email" type="email" placeholder="you@company.com" value={emailVal}
                onChange={(e) => setEmailVal(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && emailVal) sendEmail(); }}
                data-testid="brochure-email-input" />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setEmailOpen(false)} className="rounded-full">Cancel</Button>
              <Button onClick={sendEmail} disabled={!emailVal || sending} data-testid="brochure-email-send" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                {sending ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Sending…</> : <><Mail className="h-4 w-4 mr-1.5" />Send brochure</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <main className="max-w-6xl mx-auto px-5 pb-24">
          {/* Industry selector */}
          <div className="pt-6 flex flex-wrap items-center gap-2 print:hidden" data-testid="industry-selector">
            <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold mr-1">Tailored for</span>
            {d.industries.map((ind) => (
              <button
                key={ind.key}
                data-testid={`industry-tab-${ind.key}`}
                onClick={() => setIndustry(ind.key)}
                className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${industry === ind.key ? "bg-primary text-primary-foreground shadow-sm" : "bg-card/70 border border-border text-muted-foreground hover:text-foreground hover:border-primary/40"}`}
              >
                {ind.label}
              </button>
            ))}
          </div>

          {/* Hero */}
          <section className="pt-8 pb-10 grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
            <motion.div {...reveal}>
              <Badge variant="outline" className="rounded-full gap-1.5 mb-4 text-[11px]"><Sparkles className="h-3 w-3 text-gold" />{d.hero.eyebrow} · v{d.meta.version}</Badge>
              <h1 className="font-display text-4xl sm:text-5xl leading-[1.05] tracking-tight">{d.hero.title}</h1>
              <p className="mt-5 text-lg text-muted-foreground leading-relaxed max-w-xl">{d.hero.subtitle}</p>
              <div className="mt-6 flex flex-wrap gap-2">
                <Button onClick={download} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="brochure-download-hero"><Download className="h-4 w-4 mr-1.5" />Download the brochure</Button>
                <Button variant="outline" onClick={share} className="rounded-full"><Share2 className="h-4 w-4 mr-1.5" />Share</Button>
              </div>
            </motion.div>
            <motion.div {...reveal} className="relative">
              <ProductVisual metrics={d.metrics} />
            </motion.div>
          </section>

          {/* Live metrics */}
          <motion.section {...reveal} className="grid grid-cols-2 lg:grid-cols-4 gap-3 py-6">
            {d.metrics.map((m) => (
              <div key={m.label} className="rounded-2xl border border-border bg-card/70 p-5">
                <p className="font-display text-3xl tracking-tight text-primary">{m.value}</p>
                <p className="text-sm font-medium mt-1">{m.label}</p>
                <p className="text-[11px] text-muted-foreground">{m.sub}</p>
              </div>
            ))}
          </motion.section>

          {/* Product at a glance */}
          {d.overview && (
            <section className="py-10" data-testid="overview-section">
              <motion.div {...reveal} className="mb-6">
                <p className="text-xs uppercase tracking-[0.22em] text-primary font-semibold mb-2">Product at a glance</p>
                <h2 className="font-display text-3xl">One governed operating system for all of enterprise AI.</h2>
              </motion.div>
              <motion.div {...reveal} className="rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
                <p className="font-display text-xl leading-relaxed text-foreground">{d.overview.what}</p>
                <p className="text-sm text-muted-foreground mt-3"><b className="text-foreground">Who it's for.</b> {d.overview.who}</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
                  {d.overview.pillars.map((p) => (
                    <div key={p.name} className="pl-3.5 border-l-2 border-primary">
                      <p className="font-semibold text-foreground text-sm">{p.name}</p>
                      <p className="text-[13px] text-muted-foreground leading-snug mt-0.5">{p.desc}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
              {d.frameworks && (
                <motion.div {...reveal} className="mt-4 rounded-xl border border-border border-l-[3px] border-l-gold bg-primary/5 px-5 py-3.5 flex flex-wrap items-center gap-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-primary mr-1">Designed to support</span>
                  {d.frameworks.map((f) => (
                    <span key={f} className="text-xs rounded-full bg-background border border-border px-2.5 py-1 text-foreground/80">{f}</span>
                  ))}
                </motion.div>
              )}
            </section>
          )}

          {/* Problem */}
          <motion.section {...reveal} className="py-12 max-w-3xl">
            <p className="text-xs uppercase tracking-[0.22em] text-primary font-semibold mb-3">The gap in the market</p>
            <h2 className="font-display text-2xl sm:text-3xl leading-snug">{d.problem.title}</h2>
            <p className="mt-4 text-muted-foreground leading-relaxed text-[15px]">{d.problem.body}</p>
          </motion.section>

          {/* Differentiators */}
          <section className="py-8">
            <motion.p {...reveal} className="text-xs uppercase tracking-[0.22em] text-primary font-semibold mb-4">A category of one</motion.p>
            <div className="grid md:grid-cols-2 gap-4">
              {d.differentiators.map((df, i) => (
                <motion.div key={df.title} {...reveal} transition={{ ...reveal.transition, delay: i * 0.05 }} className="rounded-2xl border border-border bg-card/60 p-5 flex gap-3">
                  <span className="h-8 w-8 rounded-lg bg-primary/12 text-primary flex items-center justify-center shrink-0"><Check className="h-4 w-4" /></span>
                  <div><h3 className="font-display text-lg leading-tight">{df.title}</h3><p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{df.body}</p></div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Comparison */}
          {d.comparison && (
            <section className="py-10">
              <motion.div {...reveal} className="mb-6">
                <p className="text-xs uppercase tracking-[0.22em] text-primary font-semibold mb-2">How the approaches compare</p>
                <h2 className="font-display text-3xl">One control plane vs. the status quo.</h2>
              </motion.div>
              <motion.div {...reveal} className="overflow-x-auto rounded-2xl border border-border shadow-sm">
                <table className="w-full text-sm min-w-[640px]" data-testid="comparison-table">
                  <thead>
                    <tr className="bg-[hsl(var(--panel))] text-white">
                      <th className="text-left font-medium p-4">Capability</th>
                      {d.comparison.columns.map((c, i) => (
                        <th key={c} className={`p-4 text-center font-semibold ${i === 0 ? "bg-primary text-primary-foreground" : ""}`}>{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {d.comparison.rows.map((r, ri) => (
                      <tr key={r.dim} data-testid={`compare-row-${ri}`} className={ri % 2 ? "bg-card/40" : ""}>
                        <td className="p-4 text-foreground/90 border-t border-border font-medium">{r.dim}</td>
                        {r.v.map((v, ci) => (
                          <td key={ci} className={`p-4 text-center border-t border-border ${ci === 0 ? "bg-primary/5" : ""}`}>
                            <CompareCell v={v} primary={ci === 0} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </motion.div>
            </section>
          )}

          {/* Capabilities */}
          <section className="py-10">
            <motion.div {...reveal} className="mb-6">
              <p className="text-xs uppercase tracking-[0.22em] text-primary font-semibold mb-2">Capabilities</p>
              <h2 className="font-display text-3xl">Everything you need to govern AI — in one OS.</h2>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-4">
              {d.capabilities.map((c, i) => {
                const Icon = ICONS[c.icon] || Network;
                return (
                  <motion.div key={c.key} {...reveal} transition={{ ...reveal.transition, delay: (i % 2) * 0.05 }} data-testid={`cap-${c.key}`} className="rounded-2xl border border-border bg-card/70 p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="h-11 w-11 rounded-xl bg-primary/12 text-primary flex items-center justify-center shrink-0"><Icon className="h-5 w-5" /></span>
                      <h3 className="font-display text-xl leading-tight">{c.name}</h3>
                    </div>
                    <p className="text-sm text-foreground/80 leading-relaxed">{c.tagline}</p>
                    <ul className="mt-3 space-y-1.5">
                      {c.points.map((p) => <li key={p} className="flex items-start gap-2 text-sm text-muted-foreground"><Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />{p}</li>)}
                    </ul>
                  </motion.div>
                );
              })}
            </div>
          </section>

          {/* Business case — ROI */}
          {d.roi && (
            <section className="py-10">
              <motion.div {...reveal} className="mb-6">
                <p className="text-xs uppercase tracking-[0.22em] text-primary font-semibold mb-2">The business case</p>
                <h2 className="font-display text-3xl">Spend you can defend — and prove.</h2>
              </motion.div>
              <div className="grid lg:grid-cols-[1.25fr_0.75fr] gap-5 items-start">
                {/* ROI value table */}
                <motion.div {...reveal} className="rounded-2xl border border-border overflow-hidden bg-card/60" data-testid="roi-table">
                  <div className="flex items-center justify-between px-5 py-3 bg-[hsl(var(--panel))] text-white">
                    <span className="text-sm font-semibold">Value driver</span>
                    <span className="text-sm font-semibold capitalize">{d.roi.period_label}</span>
                  </div>
                  <div className="divide-y divide-border">
                    {d.roi.rows.map((r) => {
                      const neg = r.amount < 0;
                      const amt = `${neg ? "-" : "+"}$${Math.abs(r.amount).toLocaleString()}`;
                      return (
                        <div key={r.label} className="flex items-center justify-between gap-3 px-5 py-3">
                          <div>
                            <p className="text-sm font-semibold text-foreground">{r.label}</p>
                            <p className="text-[11px] text-muted-foreground">{r.note}</p>
                          </div>
                          <span className="text-base font-bold tabular-nums" style={{ color: neg ? "hsl(var(--sev-warning))" : "hsl(var(--sev-success))" }}>{amt}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center justify-between px-5 py-3.5 bg-primary text-primary-foreground">
                    <span className="text-sm font-semibold">Net value, after platform cost</span>
                    <span className="text-xl font-bold tabular-nums">+${d.roi.net_value.toLocaleString()}</span>
                  </div>
                </motion.div>
                {/* Visuals */}
                <motion.div {...reveal} className="space-y-4">
                  <div className="rounded-2xl border border-border bg-card/60 p-5">
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mb-3">Return on every $1</p>
                    <div className="flex items-center gap-3 mb-2.5">
                      <span className="h-4 rounded bg-[hsl(var(--panel))]" style={{ width: 28 }} />
                      <span className="text-sm text-foreground">Invested <b>$1</b></span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="h-4 rounded bg-primary" style={{ width: `${Math.min((d.roi.roi_multiple / 12) * 100, 88)}%` }} />
                      <span className="text-sm font-semibold text-primary whitespace-nowrap">Returned ${d.roi.roi_multiple}</span>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-3 italic">A {d.roi.roi_multiple}x return on the platform investment.</p>
                  </div>
                  <div className="rounded-2xl border border-border bg-card/60 p-5 flex items-center gap-4">
                    <ComplianceDonut score={complianceScore} />
                    <div>
                      <p className="text-sm font-semibold text-foreground">Policy effectiveness</p>
                      <p className="text-[11px] text-muted-foreground leading-snug">Across every vendor, scored in real time.</p>
                    </div>
                  </div>
                </motion.div>
              </div>
              <p className="text-[11px] text-muted-foreground mt-4 flex items-center gap-1.5">
                <RefreshCw className="h-3 w-3" />Figures are generated from the current period's platform data for this organisation.
              </p>
            </section>
          )}

          {/* Trust — dark panel */}
          <motion.section {...reveal} className="my-10 rounded-[26px] bg-[hsl(var(--panel))] text-white p-8 sm:p-10 relative overflow-hidden">
            <span className="absolute inset-x-0 top-0 h-[3px]" style={{ background: "linear-gradient(90deg, hsl(var(--panel-accent)), transparent)" }} />
            <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--panel-accent))] font-semibold mb-3">Trust, enforced</p>
            <h2 className="font-display text-2xl sm:text-3xl">{d.trust.title}</h2>
            <div className="grid sm:grid-cols-2 gap-3 mt-5">
              {d.trust.points.map((p) => <div key={p} className="flex items-start gap-2 text-[15px] text-white/85"><ShieldCheck className="h-4.5 w-4.5 text-[hsl(var(--panel-accent))] mt-0.5 shrink-0" style={{ height: 18, width: 18 }} />{p}</div>)}
            </div>
          </motion.section>

          {/* CTA — dark panel */}
          <motion.section {...reveal} className="mt-10 rounded-[26px] bg-[hsl(var(--panel))] text-white p-10 text-center relative overflow-hidden">
            <LogoWhite className="h-12 mx-auto mb-5" />
            <h2 className="font-display text-3xl max-w-2xl mx-auto">{d.cta.title}</h2>
            <p className="text-white/70 mt-3 max-w-xl mx-auto">{d.cta.body}</p>
            <div className="mt-6 flex flex-wrap gap-2 justify-center">
              <Button asChild size="lg" className="rounded-full bg-white text-[hsl(var(--panel))] hover:bg-white/90"><Link to="/login" data-testid="brochure-cta-launch">{d.cta.action} <ArrowRight className="h-4 w-4 ml-1.5" /></Link></Button>
              <Button variant="outline" size="lg" onClick={download} className="rounded-full border-white/30 text-white hover:bg-white/10"><Download className="h-4 w-4 mr-1.5" />Download PDF</Button>
              <Button variant="outline" size="lg" onClick={() => setEmailOpen(true)} data-testid="brochure-cta-email" className="rounded-full border-white/30 text-white hover:bg-white/10"><Mail className="h-4 w-4 mr-1.5" />Email it to me</Button>
            </div>
          </motion.section>

          <p className="text-center text-xs text-muted-foreground mt-8">Navitra OS · v{d.meta.version} · Tailored for {d.industry.label} · {d.meta.updated}</p>
        </main>
      </div>
    </div>
  );
}

/* A screenshot-style live product visual (theme-aware, no binary assets). */
function ProductVisual({ metrics }) {
  const rows = [
    { t: "summarise_document", v: "Anthropic", d: "allowed", c: "hsl(var(--sev-success))" },
    { t: "contract_analysis", v: "OpenAI", d: "redacted", c: "hsl(var(--sev-warning))" },
    { t: "code_generation", v: "Microsoft", d: "blocked", c: "hsl(var(--sev-danger))" },
    { t: "customer_response", v: "Google", d: "coached", c: "hsl(var(--sev-info))" },
  ];
  return (
    <div className="relative">
      <motion.div className="absolute -inset-5 rounded-[2rem] blur-2xl" style={{ background: "radial-gradient(60% 60% at 50% 20%, hsl(var(--primary)/0.18), transparent 65%)" }} animate={{ opacity: [0.6, 1, 0.6] }} transition={{ duration: 5, repeat: Infinity }} />
      <motion.div animate={{ y: [0, -8, 0] }} transition={{ duration: 6.5, repeat: Infinity, ease: "easeInOut" }} className="relative rounded-[22px] bg-card border border-border shadow-[0_40px_90px_-45px_hsl(var(--panel)/0.5)] overflow-hidden">
        <div className="flex items-center gap-2 h-10 px-4 border-b border-border/70">
          <Circle className="h-2.5 w-2.5 fill-[#ef6a5e] text-[#ef6a5e]" /><Circle className="h-2.5 w-2.5 fill-[#f5be4f] text-[#f5be4f]" /><Circle className="h-2.5 w-2.5 fill-[hsl(var(--primary))] text-[hsl(var(--primary))]" />
          <span className="ml-2 text-xs text-muted-foreground">Navitra OS · Cockpit</span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-semibold text-[hsl(var(--primary))]"><motion.span className="h-2 w-2 rounded-full bg-[hsl(var(--primary))]" animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 1.6, repeat: Infinity }} />LIVE</span>
        </div>
        <div className="p-4 grid grid-cols-2 gap-2">
          {metrics.slice(0, 4).map((m) => (
            <div key={m.label} className="rounded-xl bg-secondary/50 border border-border/60 p-3">
              <p className="font-display text-xl text-primary leading-none">{m.value}</p>
              <p className="text-[10.5px] text-muted-foreground mt-1">{m.label}</p>
            </div>
          ))}
        </div>
        <div className="px-4 pb-4 space-y-1.5">
          {rows.map((r) => (
            <div key={r.t} className="flex items-center gap-2 text-[12px]">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: r.c }} />
              <span className="font-medium truncate">{r.t.replace(/_/g, " ")}</span>
              <span className="text-muted-foreground ml-auto">{r.v}</span>
              <span className="rounded-full px-2 py-0.5 text-[10px] font-medium capitalize" style={{ color: r.c, background: `color-mix(in srgb, ${r.c} 12%, transparent)` }}>{r.d}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

/* Comparison cell badge */
function CompareCell({ v, primary }) {
  if (v === "full")
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold text-white" style={{ background: primary ? "hsl(var(--primary))" : "hsl(var(--sev-success))" }}>
        <Check className="h-3 w-3" />Yes
      </span>
    );
  if (v === "partial")
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold text-white bg-gold">
        <Minus className="h-3 w-3" />Partial
      </span>
    );
  if (v === "none")
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium text-muted-foreground bg-secondary">
        <X className="h-3 w-3" />No
      </span>
    );
  return <span className={`text-xs font-semibold ${primary ? "text-primary" : "text-muted-foreground"}`}>{v}</span>;
}

/* Compliance donut gauge */
function ComplianceDonut({ score }) {
  const r = 26, c = 2 * Math.PI * r, pct = Math.max(0, Math.min(100, score));
  return (
    <div className="relative h-[72px] w-[72px] shrink-0" data-testid="compliance-donut">
      <svg viewBox="0 0 72 72" className="h-full w-full -rotate-90">
        <circle cx="36" cy="36" r={r} fill="none" strokeWidth="8" className="stroke-secondary" />
        <circle cx="36" cy="36" r={r} fill="none" strokeWidth="8" strokeLinecap="round" stroke="hsl(var(--primary))" strokeDasharray={c} strokeDashoffset={c - (pct / 100) * c} />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-display text-sm font-bold text-primary">{Math.round(pct)}%</span>
    </div>
  );
}
